import { useId, useMemo, useState } from 'react';

import { DateInput } from 'cpool-components/input/DateInput';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { TextInput } from 'cpool-components/input/TextInput';
import { getLastWorkdayOfDate } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import type { Adviser_Schema } from './types/AdviserSchema';
import { dateFormat } from '~/lib/i18n';

type Props = {
    absenceFrom: Date;
    absenceUntil: Date;
    delegates?: Adviser_Schema[];
    isLoading: boolean;
    setAbsenceFrom: (date: Date) => void;
    setAbsenceUntil: (date: Date) => void;
    setDelegate: (adviser: Adviser_Schema) => void;
};

export const AdviserAbsenceEdit: React.FC<Props> = ({
    absenceFrom,
    absenceUntil,
    delegates,
    setAbsenceFrom,
    setAbsenceUntil,
    setDelegate,
}) => {
    const { t } = useTranslation();
    const delegateLabelId = useId();

    const [selectedDelegateId, setSelectedDelegateId] = useState<string | undefined>();

    const delegateOptions = useMemo(
        () => delegates?.map((adviser) => ({ label: adviser.name, value: adviser.id })) ?? [],
        [delegates],
    );

    const rangeValue = { from: absenceFrom, to: absenceUntil };

    const handleRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
        if (range?.from) setAbsenceFrom(range.from);
        if (range?.to) setAbsenceUntil(range.to);
    };

    return (
        <div className="py-3 space-y-2">
            <div>
                <p className="text-sm text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.ADD_SUBTITLE')}</p>
            </div>
            <div className="grid grid-cols-2 gap-1">
                <div className="flex items-center">
                    <p className="text-sm text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.FROM')}</p>
                </div>
                <div className="min-w-0">
                    <DateInput
                        id="adviserAbsenceRange"
                        mode="range"
                        value={rangeValue}
                        onChange={handleRangeChange}
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-1">
                <div className="flex items-center">
                    <p className="text-sm text-midnight">{t('DASHBOARD.ADVISER_ABSENCE.LEADTRACK_DISABLED')}</p>
                </div>
                <div>
                    <TextInput
                        id="adviserAbsenceTrackDisabled"
                        value={absenceFrom && dateFormat(getLastWorkdayOfDate(absenceFrom))}
                        disabled
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-1">
                <div className="flex items-center">
                    <p className="text-sm text-midnight" id={delegateLabelId}>
                        {t('DASHBOARD.ADVISER_ABSENCE.DELEGATE')}
                    </p>
                </div>
                <div>
                    <DropdownMenu
                        aria-labelledby={delegateLabelId}
                        options={delegateOptions}
                        searchable
                        value={selectedDelegateId}
                        triggerClassName="w-full"
                        minPanelWidth={240}
                        onChange={(value: string | string[]) => {
                            const id = Array.isArray(value) ? value[0] : value;
                            setSelectedDelegateId(id);
                            const adviser = delegates?.find((d) => d.id === id);
                            if (adviser) setDelegate(adviser);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
